import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import type { SignInResponse, SsoSignInResponse } from '@bliks-io/contracts';
import { AuthPageLayout } from '@/components/auth/AuthPageLayout';
import { AuthResultState } from '@/components/auth/AuthResultState';
import { exchangeSsoCode } from '@/api/mutations/useAuth';
import { membershipsQueryOptions } from '@/api/queries/useMemberships';
import { ROUTES } from '@/constants/routes';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { redirectBrowserReplacing } from '@/lib/browser-redirect';
import { logger } from '@/lib/logger';
import { signedInDestination } from '@/lib/auth/signedInDestination';
import { translateErrorMessage } from '@/lib/errors/error-formatter';
import type { ApiError } from '@/lib/agent.types';

/**
 * Where the fresh SSO session lands: identities with several workspaces get
 * the picker, everyone else goes straight in. fetchQuery (not just fetch)
 * prewarms the cache the picker renders from. Fails open to the default
 * destination — a broken memberships endpoint must not block login.
 */
async function postSignInDestination(queryClient: QueryClient, session: SignInResponse) {
  try {
    const { memberships } = await queryClient.fetchQuery(membershipsQueryOptions);
    if (memberships.length > 1) {
      return { to: '/select-workspace' } as const;
    }
  } catch {
    // Fall through to the session's own destination.
  }
  return signedInDestination(session);
}

interface AuthCallbackPageProps {
  code: string;
  state: string;
}

/**
 * Codes and states are single-use on the backend, so re-firing consumes an
 * already-spent state and fails with 401. Caching the exchange PROMISE (not
 * just a fired-flag) keyed by `state` lets every mounted instance join the
 * same in-flight exchange: whichever instance is alive when it settles shows
 * the outcome. A flag-only guard hung the page forever on failure — the
 * StrictMode remount (or any future remount) destroyed the react-query
 * observer that fired the request, so its error state died with it while the
 * flag blocked a re-fire. A fresh login attempt carries a new state, so it is
 * never blocked.
 *
 * A single slot suffices: only the current state's entry can ever be read
 * again (each arrival is a fresh document via the IdP redirect, and spent
 * callback URLs are replaced out of history). Retaining the settled promise
 * is deliberate — evicting it would let a remount re-send the spent code.
 * Don't move the exchange into the route loader either: loaders re-run on
 * preload and back-nav, so the same cache would be needed there anyway.
 */
let cachedExchange: { state: string; promise: Promise<SsoSignInResponse> } | null = null;

/**
 * The exchange can also reject with non-API failures (storage quota in
 * setAuth, a navigation throw) — narrow at runtime instead of casting so the
 * error UI renders SOMETHING for every shape; an eternal spinner is the one
 * unacceptable outcome here.
 */
function asApiError(error: unknown): ApiError {
  if (error && typeof error === 'object' && 'statusCode' in error) {
    return error as ApiError;
  }
  return {
    message: error instanceof Error ? error.message : String(error),
    statusCode: 0,
    error: 'Unknown Error'
  };
}

/**
 * Completes SSO sign-in after Zitadel redirects back with code + state.
 * On failure the user restarts from /login.
 */
export function AuthCallbackPage({ code, state }: AuthCallbackPageProps) {
  const { t } = useTranslation();
  useDocumentTitle('pageTitles.login');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [exchangeError, setExchangeError] = useState<ApiError | null>(null);

  useEffect(
    function joinExchangeForState() {
      const cached =
        cachedExchange?.state === state
          ? cachedExchange
          : (cachedExchange = { state, promise: exchangeSsoCode({ code, state }) });

      // Only the currently mounted instance may navigate or render the error —
      // an unmounted instance acting on the settled exchange would double-fire
      // navigation or setState on a dead component.
      let disposed = false;
      void (async () => {
        let session: SsoSignInResponse;
        try {
          session = await cached.promise;
        } catch (error) {
          if (!disposed) setExchangeError(asApiError(error));
          return;
        }

        try {
          if (disposed) return;
          // A step-up return must land exactly where the 403 interrupted the
          // user — full page load like workspace switch (setAuth already
          // persisted the fresh token, the new document boots from it) — and
          // must NOT run the multi-workspace picker logic. Replacing keeps
          // the spent code+state out of history: a Back that reloads the
          // callback document would re-fire the exchange with a spent state.
          if (session.returnTo) {
            redirectBrowserReplacing(session.returnTo);
            return;
          }
          const destination = await postSignInDestination(queryClient, session);
          // Re-check after each await: this instance may have unmounted
          // during the fetch, and the live instance navigates.
          if (disposed) return;
          await navigate({ ...destination, replace: true });
        } catch (error) {
          logger.error('SSO post-sign-in navigation failed', error);
          if (!disposed) setExchangeError(asApiError(error));
        }
      })();
      return () => {
        disposed = true;
      };
    },
    [code, state, navigate, queryClient]
  );

  const errorMessage = exchangeError ? translateErrorMessage(exchangeError, t) : null;

  if (errorMessage) {
    return (
      <AuthPageLayout description={t('ssoSignInFailed')}>
        <AuthResultState
          description={errorMessage}
          // replace: Back from /login must not return to the spent callback
          // entry — it can only ever fail again.
          onBackToLogin={() => navigate({ to: ROUTES.LOGIN, replace: true })}
        />
      </AuthPageLayout>
    );
  }

  return (
    <AuthPageLayout description={t('ssoSigningIn')}>
      <div className="flex justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    </AuthPageLayout>
  );
}
